import { useState } from 'react';
import { SelectField, TextField } from './FormField';
import { EMERGENCY_CONTACT_RELATIONS } from '../services/team';

/**
 * Dropdown for an emergency-contact relation (Spouse / Partner / Parent /
 * Child / Other). Picking "Other" reveals a free-text input so the team
 * member can type anything the predefined list doesn't cover (Friend,
 * Grandparent, etc.) without a schema change.
 *
 * Controlled component: `value` is the final stored string and `onChange`
 * fires every time it changes. Internally we keep a small `otherSelected`
 * flag so "Other" stays selected in the UI even when the free-text field
 * is momentarily empty — without it, backspacing the text would collapse
 * the UI back to "no selection" mid-edit.
 *
 * Storage format on the Team record:
 *   - One of the predefined list → value stored verbatim ("Spouse")
 *   - "Other" + typed text       → typed text stored verbatim ("Friend")
 *   - "Other" + empty text       → value stays "Other" (or "" if the user
 *                                  hasn't entered this form yet)
 * No schema change — all downstream consumers read a plain string.
 */
export default function RelationField({
  label = 'Relation',
  value,
  onChange,
  error,
  required,
}) {
  const isPredefined = (v) =>
    EMERGENCY_CONTACT_RELATIONS.includes(v) && v !== 'Other';

  // Remember the user's explicit "Other" selection so an empty text field
  // doesn't reset the dropdown back to "— select —" while they're typing.
  // Seeded from the incoming value: anything that isn't in the predefined
  // list is treated as an Other value on first render.
  const [otherSelected, setOtherSelected] = useState(() =>
    value ? !isPredefined(value) : false,
  );

  // Derive display state from value + otherSelected flag.
  let choice = '';
  if (value && isPredefined(value)) {
    choice = value;
  } else if (value || otherSelected) {
    choice = 'Other';
  }
  // The text field only shows a "custom" stored string. Literal "Other"
  // (seen when the user picked Other but hasn't typed yet) renders blank
  // so the placeholder takes over.
  const other =
    value && !isPredefined(value) && value !== 'Other' ? value : '';

  const onChoiceChange = (next) => {
    if (next === 'Other') {
      setOtherSelected(true);
      // If we were holding a predefined value, clear it — the Other text
      // field starts empty so the user can type fresh. A custom value
      // already stored (e.g. "Friend") is left alone so switching to
      // Other doesn't lose their work.
      if (isPredefined(value)) onChange('');
    } else {
      setOtherSelected(false);
      onChange(next);
    }
  };

  const onOtherChange = (next) => {
    setOtherSelected(true);
    onChange(next);
  };

  return (
    <>
      <SelectField
        label={label + (required ? ' *' : '')}
        value={choice}
        onChange={onChoiceChange}
        options={EMERGENCY_CONTACT_RELATIONS}
        error={choice !== 'Other' ? error : undefined}
      />
      {choice === 'Other' && (
        <TextField
          label="Other Relation"
          value={other}
          onChange={onOtherChange}
          placeholder="e.g. Friend, Grandparent"
          error={error}
        />
      )}
    </>
  );
}
